import { create } from 'zustand';
import { parseDay } from '../utils/calendarDate';
import { record } from '../observability';
import { local } from '../lib/safeStorage';
import { format as fnsFormat } from 'date-fns';
import { lt as ltLocale } from 'date-fns/locale/lt';
import { enGB } from 'date-fns/locale/en-GB';
import en from './en';
import lt from './lt';

type Lang = 'en' | 'lt';
const translations = { en, lt };
const dateLocales = { en: enGB, lt: ltLocale };

/** Shape of the translations dictionary — `en` is the authoritative schema. */
export type Translations = typeof en;

interface I18nStore {
  lang: Lang;
  t: Translations;
  setLang: (lang: Lang) => void;
}

/** `index.html` ships `<html lang="en">` and nothing used to change it, so
 *  the attribute said English while the page rendered Lithuanian. A screen
 *  reader announces the document in the wrong language, and any check that
 *  trusts the attribute is reading a constant. Keep it equal to what is on
 *  screen — at start and on every change. */
function syncDocumentLang(lang: Lang): Lang {
  try { document.documentElement.lang = lang; } catch { /* no DOM in a test */ }
  return lang;
}

// #457 — guarded through `lib/safeStorage.ts`: when the browser blocks site
// data, a bare read throws at module load, before React mounts, and the page
// is blank.
function readStoredLang(): Lang {
  // A stored value other than 'en' or 'lt' gives 'lt'. Without this check, `t` is undefined and the first text throws.
  const stored = local.get('lang');
  return stored === 'en' || stored === 'lt' ? stored : 'lt';
}
const initialLang: Lang = syncDocumentLang(readStoredLang());

export const useI18n = create<I18nStore>((set) => ({
  lang: initialLang,
  t: translations[initialLang],
  setLang: (lang) => {
    local.set('lang', lang); // With the storage off, the choice lasts for this page.
    syncDocumentLang(lang);
    record({ event: 'language_changed', props: { lang } });
    set({ lang, t: translations[lang] });
  },
}));

/** Format an INSTANT using the current i18n locale, in the browser's timezone.
 *
 *  For a CALENDAR DATE use `formatDay` instead. `new Date('2026-09-16')` is
 *  parsed as UTC midnight by the ECMAScript date-only form, so formatting it
 *  here renders the 15th for any viewer west of Greenwich. See #322. */
export function formatDate(date: Date | string | number, pattern: string): string {
  const lang = useI18n.getState().lang;
  const d = typeof date === 'string' ? new Date(date) : date;
  return fnsFormat(d, pattern, { locale: dateLocales[lang] });
}

/** Format a CALENDAR DATE — 'YYYY-MM-DD' — using the current i18n locale.
 *
 *  A calendar date has no timezone: a bout on 16 September is on the 16th
 *  everywhere. `parseDay` builds local midnight from the parts, so the day the
 *  caller passed is the day that renders, in every timezone. */
export function formatDay(day: string | Date | null | undefined, pattern: string): string {
  if (!day) return '';
  const lang = useI18n.getState().lang;
  const d = day instanceof Date ? day : parseDay(String(day));
  if (Number.isNaN(d.getTime())) return '';
  return fnsFormat(d, pattern, { locale: dateLocales[lang] });
}
